/**
 * The PDP shaped selection set.
 *
 * `customAttributes` is selected at the top level. The identical field under `attributes` is
 * deprecated in the UPS schema in favour of this one.
 *
 * Notable omissions, all deliberate:
 * - `priceInfo`, because it is major units where the PDP works in minor units via `prices`.
 * - `options`, because they cannot move off cat-service-api until the Mononoth emitter stops
 *   collapsing image options to `selection` and UPS carries computed percentage prices. Their
 *   coverage is already reported by `partnerInfo` and `specialFeatures`, which share the one
 *   product-event write path.
 * - `redirectUrl`, which exists in the schema but has no population path, so it is always null.
 * - `attributes.deliveryFree` and the top-level `freeDomesticDelivery`, both deprecated in favour
 *   of the windows-derived `delivery.freeDomesticDelivery`.
 */
export const unifiedProductQuery = `
query UnifiedProduct($productUri: String!) {
  product(by: { uri: $productUri }) {
    allowsGiftMessage
    allowsGiftReceipt
    attributes {
      allowsGiftWrap
      highlyRated
      starBrand
    }
    available
    backInStockAt
    breadcrumbCategoryId
    code
    customAttributes {
      key
      value {
        ... on StringValue {
          value
        }
        ... on NumberValue {
          numValue: value
        }
        ... on BooleanValue {
          boolValue: value
        }
        ... on ArrayValue {
          values
        }
      }
      attributeType
    }
    delivery {
      deliveryByEvent {
        arriveByEvent
        eventName
        lastDeliveryBy
      }
      freeDomesticDelivery
    }
    description
    dimensions
    freeGiftWrap
    images {
      altText
      gravity
      height
      href
      position
      title
      uri
      width
    }
    ingredients {
      href
      rel
      title
    }
    inventoryStatus
    isNew
    materials
    metaDescription
    metaRobotsIndexable
    navigation {
      options {
        href
        rel
        title
      }
      title
      type
    }
    onSale
    otherInformation
    partnerInfo {
      id
      links {
        href
        rel
        title
        type
      }
      location
      logo
      metaTagDescription
      name
      shortcode
    }
    preSalePrices {
      amount
      currency
      exponent
    }
    prices {
      amount
      currency
      exponent
    }
    productLinks {
      href
      rel
      title
      type
    }
    promotions {
      endDate
      id
    }
    purchasable
    ratings {
      averageRating
      ratingCount
    }
    rawAvailability
    salePercentage
    shippingInfo
    specialFeatures {
      ecoFriendly
      exclusive
      madeInBritain
      personalisable
      requiresPhotoUpload
    }
    title
    variationsAvailable
  }
}
`;
